import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DataTable } from "../ui";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { api } from "../../services";
import {
  fetchTrackHubBranches,
  fetchTrackHubCarriers,
  patchTrackHubBranch,
} from "../../services/trackHubApi";
import {
  resolveDateLocale,
  formatLocalizedDateTimeParts,
} from "../../utils/formatDateTime";
import { getLocalizedPlatformName } from "../../utils/platformNames";
import { resolveIntegrationLogo } from "../../utils/platformLogos";
import {
  TrackHubActivateSelect,
  TrackHubShipmentSelect,
} from "./TrackHubBranchSelects";
import TrackHubCarriersTab from "./TrackHubCarriersTab";
import TrackHubDeliveryCostsTab from "./TrackHubDeliveryCostsTab";
import { usePermission } from "../../hooks/usePermission";
import { RBAC_MODULES, PERMISSION_ACTIONS } from "../../config/routePermissions";
import "./trackhub-branches.css";
import "./trackhub-carriers.css";
import "./trackhub-delivery-costs.css";
import "../subscription/SubscriptionFlow.css";
import SubscriptionConnectBanner from "../subscription/SubscriptionConnectBanner";

const PAGE_TABS = [
  { id: "branches", i18n: "trackHubBranchesPage.tabs.branches" },
  { id: "carriers", i18n: "trackHubBranchesPage.tabs.carriers" },
  { id: "deliveryCosts", i18n: "trackHubBranchesPage.tabs.deliveryCosts" },
];

/** Synthetic Activate option — not an AvailableApp; persisted as allowManualShipment. */
const MANUAL_SHIPMENT_TYPE = "MANUAL_SHIPMENT";

const toScopeId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value._id || value.id || "";
};

function carriersForAssign(accountCarriers, row) {
  const list = Array.isArray(accountCarriers) ? [...accountCarriers] : [];
  const id = String(row?.assignedCarrierId || "").toUpperCase();
  if (!id) return list;
  const present = list.some(
    (carrier) => String(carrier.carrierId || carrier.id || "").toUpperCase() === id
  );
  if (present) return list;
  return [
    { carrierId: id, name: row.assignedCarrierName || id },
    ...list,
  ];
}

function TrackHubBranchesPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("translation");
  const { can } = usePermission();
  const canUpdate = can(RBAC_MODULES.TRACKHUB_SETTINGS, PERMISSION_ACTIONS.EDIT);
  const isArabic = i18n.language === "ar";
  const dateLocale = resolveDateLocale(i18n.language);

  const [activeTab, setActiveTab] = useState("branches");
  const [carriersVisitKey, setCarriersVisitKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [connectedAddons, setConnectedAddons] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [storesOptions, setStoresOptions] = useState([]);
  const [activeByStore, setActiveByStore] = useState({});
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({});
  const [savingRow, setSavingRow] = useState(null);
  const [tableSortColumn, setTableSortColumn] = useState("branch");
  const [sortDirection, setSortDirection] = useState("asc");
  const [accountCarriers, setAccountCarriers] = useState([]);

  const loadStores = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await api.get("/stores/my", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const stores = res.data?.stores || [];
      const options = stores.map((s) => {
        const nameEn = (s?.name && typeof s.name === "object" ? s.name.en : s.name) || s.name_en || s.displayName || "";
        const nameAr = (s?.name && typeof s.name === "object" ? s.name.ar : null) || s.name_ar || s.displayNameAr || nameEn;
        return {
          _id: s._id || s.id,
          name_en: nameEn,
          name_ar: nameAr,
          branches: s.branches || [],
        };
      });
      setStoresOptions(options);
      if (options[0]?._id) {
        setSelectedStoreId((current) => current || options[0]._id);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error("Failed to load stores", error);
      setLoading(false);
    }
  }, []);

  const loadBranches = useCallback(async () => {
    if (!selectedStoreId) return;
    setLoading(true);
    try {
      const result = await fetchTrackHubBranches({
        page,
        limit,
        storeId: toScopeId(selectedStoreId) || undefined,
      });
      setRows(result.data || []);
      setConnectedAddons(result.connectedAddons || []);
      setPagination(result.pagination || {});
    } catch (error) {
      console.error("Failed to load TrackHub branches", error);
      toast.error(t("trackHubBranchesPage.loadError"));
    } finally {
      setLoading(false);
    }
  }, [page, limit, selectedStoreId, t]);

  const loadAccountCarriers = useCallback(async () => {
    try {
      const result = await fetchTrackHubCarriers({ refresh: false });
      setAccountCarriers(
        (result.data || []).filter((c) => c.isActive !== false)
      );
    } catch (error) {
      console.error("Failed to load TrackHub carriers for assign shipment", error);
      setAccountCarriers([]);
    }
  }, []);

  const loadActiveCounts = useCallback(async () => {
    try {
      const counts = {};
      let pageNum = 1;
      let totalPages = 1;
      do {
        const result = await fetchTrackHubBranches({ page: pageNum, limit: 100 });
        (result.data || []).forEach((row) => {
          const storeId = String(row.storeId || "");
          if (!storeId || !row.isEnabled) return;
          counts[storeId] = (counts[storeId] || 0) + 1;
        });
        totalPages = result.pagination?.totalPages || 1;
        pageNum += 1;
      } while (pageNum <= totalPages && pageNum <= 20);
      setActiveByStore(counts);
    } catch (error) {
      setActiveByStore({});
    }
  }, []);

  useEffect(() => {
    loadStores();
    loadActiveCounts();
  }, [loadStores, loadActiveCounts]);

  useEffect(() => {
    loadAccountCarriers();
  }, [loadAccountCarriers]);

  useEffect(() => {
    if (activeTab === "branches" && selectedStoreId) {
      loadBranches();
    }
  }, [activeTab, loadBranches, selectedStoreId]);

  useEffect(() => {
    if (activeTab === "carriers") {
      loadAccountCarriers();
    }
  }, [activeTab, carriersVisitKey, loadAccountCarriers]);

  const localizeAddonLabel = useCallback(
    (addon) => getLocalizedPlatformName(addon.appType || addon.label, t),
    [t]
  );

  const resolveActivateOptions = useCallback(
    (row) => {
      const options = row.activateOptions?.length
        ? row.activateOptions
        : connectedAddons.map((addon) => ({
            appId: addon.appId,
            appType: addon.appType,
            label: addon.label,
          }));
      const mapped = options.map((opt) => ({
        ...opt,
        label: localizeAddonLabel(opt),
      }));
      return [
        ...mapped,
        {
          appId: MANUAL_SHIPMENT_TYPE,
          appType: MANUAL_SHIPMENT_TYPE,
          label: t("trackHubBranchesPage.manualShipments"),
        },
      ];
    },
    [connectedAddons, localizeAddonLabel, t]
  );

  const getActivateValue = useCallback((row) => {
    const sources = [...(row.orderSources || row.activatedAddons || [])];
    if (row.allowManualShipment) {
      sources.push(MANUAL_SHIPMENT_TYPE);
    }
    return sources;
  }, []);

  const handleOrderSourcesChange = useCallback(async (row, nextTypes) => {
    setSavingRow(row.branchId);
    try {
      const allowManualShipment = nextTypes.includes(MANUAL_SHIPMENT_TYPE);
      const orderSources = nextTypes.filter((type) => type !== MANUAL_SHIPMENT_TYPE);
      if (!orderSources.length && !allowManualShipment) {
        await patchTrackHubBranch(row.branchId, { enabled: false });
        toast.success(t("trackHubBranchesPage.removed"));
      } else {
        await patchTrackHubBranch(row.branchId, {
          orderSources,
          allowManualShipment,
          enabled: true,
        });
        toast.success(t("trackHubBranchesPage.updated"));
      }
      await loadBranches();
      await loadActiveCounts();
    } catch (error) {
      toast.error(t("trackHubBranchesPage.updateError"));
    } finally {
      setSavingRow(null);
    }
  }, [loadActiveCounts, loadBranches, t]);

  const handleShipmentChange = useCallback(async (row, nextValue) => {
    if (!row.isEnabled) return;
    setSavingRow(row.branchId);
    try {
      const isAuto = !nextValue || nextValue === "auto";
      await patchTrackHubBranch(row.branchId, {
        orderSources: row.orderSources || row.activatedAddons || [],
        allowManualShipment: row.allowManualShipment === true,
        shipmentAssignment: isAuto ? "auto" : "manual",
        assignedCarrierId: isAuto ? null : nextValue,
      });
      await loadBranches();
      await loadActiveCounts();
      toast.success(t("trackHubBranchesPage.updated"));
    } catch (error) {
      toast.error(t("trackHubBranchesPage.updateError"));
    } finally {
      setSavingRow(null);
    }
  }, [loadActiveCounts, loadBranches, t]);

  const selectedStore = storesOptions.find((store) => String(store._id) === String(selectedStoreId));
  const selectedStoreName = isArabic
    ? selectedStore?.name_ar || selectedStore?.name_en || ""
    : selectedStore?.name_en || selectedStore?.name_ar || "";
  const selectedBranchCount = Array.isArray(selectedStore?.branches) ? selectedStore.branches.length : 0;
  // Foodics stores are often a single branch. With no Order Agent or Digital
  // Catalog connected, Assign shipment only said "Not active" and never explained why.
  const singleBranchAddonsMissing = !loading && selectedBranchCount === 1 && connectedAddons.length === 0;

  const columns = useMemo(
    () => [
      {
        id: "branch",
        header: t("addAppPage.addAppTable.branchName"),
        sortable: true,
        strong: true,
        minWidth: 170,
        render: (row) => {
          const en = typeof row.branchName === "object"
            ? row.branchName?.en || row.branchName_en
            : row.branchName_en || row.branchName;
          const ar = typeof row.branchName === "object"
            ? row.branchName?.ar || row.branchName_ar
            : row.branchName_ar || en;
          return isArabic ? ar || en || "" : en || ar || "";
        },
      },
      {
        id: "status",
        header: t("addAppPage.addAppTable.status"),
        minWidth: 150,
        render: (row) => (
          <span className={`aa-spill ${row.isEnabled ? "ok" : "off"}`}>
            {row.isEnabled ? t("addAppPage.storeConnected") : t("addAppPage.storeNotConnected")}
          </span>
        ),
      },
      {
        id: "integratedAt",
        header: t("addAppPage.addAppTable.lastUpdated"),
        sortable: true,
        minWidth: 150,
        nowrap: true,
        render: (row) => {
          if (!row.integratedAt) return <span className="sf-hint">—</span>;
          const parts = formatLocalizedDateTimeParts(row.integratedAt, dateLocale, { withSeconds: false, fallback: "—" });
          if (!parts?.date || parts.date === "—") return <span className="sf-hint">—</span>;
          return (
            <div className="oh-table-date-cell" dir="ltr">
              <span className="oh-table-date-cell__date">{parts.date}</span>
              <span className="oh-table-date-cell__time">{parts.time}</span>
            </div>
          );
        },
      },
      {
        id: "activate",
        header: t("trackHubBranchesPage.activate"),
        minWidth: 243,
        render: (row) => (
          <TrackHubActivateSelect
            options={resolveActivateOptions(row)}
            value={getActivateValue(row)}
            inactive={!row.isEnabled && !row.allowManualShipment}
            disabled={!canUpdate || savingRow === row.branchId}
            emptyLabel={t("trackHubBranchesPage.selectSources")}
            allLabel={t("all")}
            onChange={(next) => handleOrderSourcesChange(row, next)}
          />
        ),
      },
      {
        id: "shipment",
        header: t("trackHubBranchesPage.assignShipment"),
        minWidth: 243,
        render: (row) => (
          <TrackHubShipmentSelect
            value={row.assignedCarrierId || (row.shipmentAssignment === "manual" ? (row.carriers?.[0]?.carrierId || "manual") : "auto")}
            carriers={carriersForAssign(accountCarriers, row)}
            assignedCarrierName={row.assignedCarrierName || row.carriers?.[0]?.name}
            inactive={!(row.canAssignShipment ?? row.isEnabled)}
            disabled={savingRow === row.branchId}
            notIntegratedLabel={
              singleBranchAddonsMissing
                ? t("trackHubBranchesPage.addonsNotConnected")
                : t("trackHubBranchesPage.notActive")
            }
            autoLabel={t("trackHubBranchesPage.autoAssign")}
            autoHelp={t("trackHubBranchModal.autoAssignHelp")}
            manualLabel={t("trackHubBranchesPage.manualAssign")}
            onChange={(mode) => handleShipmentChange(row, mode)}
          />
        ),
      },
    ],
    [accountCarriers, canUpdate, dateLocale, getActivateValue, handleOrderSourcesChange, handleShipmentChange, isArabic, resolveActivateOptions, savingRow, singleBranchAddonsMissing, t]
  );

  const appName = t("trackHubBranchesPage.title");
  const logoSrc = resolveIntegrationLogo({ appType: "TRACK_HUB", name: "TrackHub" });

  return (
    <div className="aa-page sf-page trackhub-branches-page-root">
      <button type="button" className="sf-btn link aa-back" onClick={() => navigate("/apps-management")}>
        ← {t("addAppPage.backToApps")}
      </button>
      <div className="aa-title">
        {logoSrc ? <img className="aa-logo" src={logoSrc} alt="" /> : <span className="aa-logo aa-logo-fallback">T</span>}
        <div>
          <h1>{appName}</h1>
          <p>{t("addAppPage.kindAddon")}</p>
        </div>
      </div>

      <div className="th-tabs" role="tablist">
        {PAGE_TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              type="button"
              key={tab.id}
              role="tab"
              aria-selected={isActive}
              className={isActive ? "is-on" : ""}
              onClick={() => {
                setActiveTab(tab.id);
                if (tab.id === "carriers") setCarriersVisitKey((k) => k + 1);
              }}
            >
              {t(tab.i18n)}
            </button>
          );
        })}
      </div>

          {activeTab === "carriers" ? (
            <TrackHubCarriersTab key={carriersVisitKey} readOnly={!canUpdate} />
          ) : activeTab === "deliveryCosts" ? (
            <TrackHubDeliveryCostsTab readOnly={!canUpdate} />
          ) : (
            <>
              <section className="sf-section">
                <div className="sf-section-head">
                  <h2>{t("addAppPage.storesTitle")}</h2>
                  <span className="sf-meta">{t("AddOnIntegration.storesCount", { count: storesOptions.length })}</span>
                </div>
                <p className="sf-sub">{t("AddOnIntegration.storesLead", { app: appName })}</p>
                <div className="aa-stores" role="group" aria-label={t("addAppPage.storesTitle")}>
                  {storesOptions.map((store) => {
                    const on = String(store._id) === String(selectedStoreId);
                    const branchCount = Array.isArray(store.branches) ? store.branches.length : 0;
                    const active = activeByStore[String(store._id)] || 0;
                    const name = isArabic ? store.name_ar || store.name_en : store.name_en || store.name_ar;
                    return (
                      <button
                        type="button"
                        key={store._id}
                        className={`aa-store${on ? " is-on" : ""}`}
                        aria-pressed={on}
                        onClick={() => {
                          setSelectedStoreId(store._id);
                          setPage(1);
                        }}
                      >
                        <span className="top"><b>{name}</b><span className="aa-radio" aria-hidden="true" /></span>
                        <small>{t("trackHubBranchesPage.activeCount", { count: active })}</small>
                        <small>{t("addAppPage.storeBranchCount", { count: branchCount })}</small>
                      </button>
                    );
                  })}
                </div>
              </section>

              <section className="sf-section aa-bsec">
                <div className="aa-bhead">
                  <div>
                    <h2>{t("addAppPage.branchesTitle")}</h2>
                    <p className="aa-bsub">
                      <b>{selectedStoreName}</b>
                      {" · "}
                      {t("trackHubBranchesPage.pickHint")}
                    </p>
                    {singleBranchAddonsMissing ? (
                      <p className="sf-hint" role="status">
                        {t("trackHubBranchesPage.addonsNotConnected")}{" "}
                        {t("trackHubBranchesPage.addonsNotConnectedHint")}
                      </p>
                    ) : null}
                  </div>
                  <SubscriptionConnectBanner appId={id} variant="meter" />
                </div>
                <DataTable
                  className="aa-branches"
                  tableId="trackhub-branches"
                  hideToolbar
                  tableMinWidth={860}
                  columns={columns}
                  data={rows}
                  rowKey={(row) => String(row.branchId)}
                  loading={loading}
                  isRtl={isArabic}
                  emptyState={t("trackHubBranchesPage.empty")}
                  sort={{
                    column: tableSortColumn,
                    direction: sortDirection,
                    onChange: ({ column, direction }) => {
                      setTableSortColumn(column);
                      setSortDirection(direction);
                    },
                  }}
                  pagination={{
                    page,
                    pageSize: limit,
                    total: pagination.total || 0,
                    onPageChange: setPage,
                    onPageSizeChange: (size) => {
                      setLimit(size);
                      setPage(1);
                    },
                    showLabel: t("show"),
                    ofLabel: t("of"),
                    resultsLabel: t("results", { defaultValue: "results" }),
                    previousLabel: t("previous"),
                    nextLabel: t("next"),
                  }}
                />
              </section>
            </>
          )}
    </div>
  );
}

export default TrackHubBranchesPage;
